import { type FC, useCallback, type ReactNode } from 'react';
import { FormattedMessage } from 'dibs-react-intl';
import { SeoLink, ALWAYS_LINKABLE_LINK } from 'dibs-elements/exports/SeoLink';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel';
import ChevronRight from 'dibs-icons/exports/ChevronRight';

import { useRecentlyViewedItemsContext, MOBILE, TABLET } from './RecentlyViewedItemsContext';
import { trackViewMoreClick } from '../recentHistoryTracking';
import { ACTION_LABEL } from './constants';

import classnames from 'classnames';
import dibsCss from 'dibs-css';

export type RecentlyViewedItemsWrapperProps = {
    wrapperClassName?: string;
    actionLabel?: string;
    trackViewMoreClick?: typeof trackViewMoreClick;
    children: ReactNode;
    hasTopSeparator?: boolean;
    hasBottomSeparator?: boolean;
};

export const RecentlyViewedItemsWrapper: FC<RecentlyViewedItemsWrapperProps> = ({
    wrapperClassName,
    children,
    actionLabel = ACTION_LABEL,
    hasTopSeparator = false,
    hasBottomSeparator = false,
    ...props
}) => {
    const { deviceType } = useRecentlyViewedItemsContext();
    const isMobile = deviceType === MOBILE;
    const isTablet = deviceType === TABLET;
    const showMobileTopSeparator = isMobile && hasTopSeparator;
    const showMobileBottomSeparator = isMobile && hasBottomSeparator;
    const onViewMoreClick = useCallback(() => {
        (props.trackViewMoreClick || trackViewMoreClick)({ actionLabel });
    }, [actionLabel, props.trackViewMoreClick]);

    const wrapperStyles = classnames(
        dibsCss.pyLarge,
        {
            [dibsCss.borderT]: !isMobile && hasTopSeparator,
            [dibsCss.borderTSolid]: !isMobile && hasTopSeparator,
            [dibsCss.borderTDolphin]: !isMobile && hasTopSeparator,
            [dibsCss.borderB]: !isMobile && hasBottomSeparator,
            [dibsCss.borderBSolid]: !isMobile && hasBottomSeparator,
            [dibsCss.borderBDolphin]: !isMobile && hasBottomSeparator,
            [dibsCss.pbMedium]: isMobile,
            [dibsCss.ptMedium]: isMobile,
        },
        wrapperClassName
    );

    const mobileSeparator = (
        <div
            className={classnames('primary-viewport-width', dibsCss.bgMoonstone, dibsCss.hXsmall)}
        />
    );

    const title = (
        <FormattedMessage
            id="RecentlyViewedItemsCarousel.title"
            defaultMessage="Still Thinking About These?"
        />
    );

    return (
        <>
            {showMobileTopSeparator && mobileSeparator}
            <div className={classnames(wrapperStyles, isMobile && 'primary-viewport-width')}>
                <div
                    className={classnames(dibsCss.flex, dibsCss.gapLarge, dibsCss.wFull, {
                        [dibsCss.itemsCenter]: !isMobile,
                        [dibsCss.flexCol]: isMobile,
                        [dibsCss.gapSmall]: isMobile || isTablet,
                    })}
                >
                    <div
                        className={classnames(dibsCss.textNoir, dibsCss.flexShrink0, {
                            [dibsCss.w200px]: !isMobile && !isTablet,
                            [dibsCss.wFull]: isMobile,
                            [dibsCss.plSmall]: isMobile,
                        })}
                        style={isTablet ? { width: 160 } : undefined}
                        data-tn="recently-viewed-header"
                    >
                        <HeadingLevel>
                            {Heading => (
                                <Heading
                                    className={classnames(
                                        dibsCss.sassyFontHeaderLarge,
                                        dibsCss.mt0,
                                        {
                                            [dibsCss.sassyFontHeaderMedium]: isMobile || isTablet,
                                            [dibsCss.mbXsmall]: !isMobile,
                                            [dibsCss.mb0]: isMobile,
                                        }
                                    )}
                                >
                                    {isMobile ? (
                                        <SeoLink
                                            buyerLinkType="standardLinkNoUnderline"
                                            variant={ALWAYS_LINKABLE_LINK}
                                            linkData={{
                                                path: '/favorites/viewed/',
                                                isLinkable: false,
                                            }}
                                            onClick={onViewMoreClick}
                                            dataTn="recently-viewed-link"
                                        >
                                            <span
                                                className={classnames(
                                                    dibsCss.inlineFlex,
                                                    dibsCss.itemsCenter,
                                                    dibsCss.gapXxsmall
                                                )}
                                            >
                                                {title}
                                                <ChevronRight variation="24" />
                                            </span>
                                        </SeoLink>
                                    ) : (
                                        <>{title}</>
                                    )}
                                </Heading>
                            )}
                        </HeadingLevel>
                        {!isMobile && (
                            <SeoLink
                                className={dibsCss.sassyFontBody}
                                variant={ALWAYS_LINKABLE_LINK}
                                linkData={{ path: '/favorites/viewed/', isLinkable: false }}
                                onClick={onViewMoreClick}
                                dataTn="recently-viewed-link"
                            >
                                <FormattedMessage
                                    id="RecentlyViewedItemsCarousel.viewAllCta"
                                    defaultMessage="All Recently Viewed"
                                />
                            </SeoLink>
                        )}
                    </div>
                    <div
                        className={classnames(dibsCss.flex1, dibsCss.minW0, dibsCss.overflowHidden)}
                    >
                        {children}
                    </div>
                </div>
            </div>
            {showMobileBottomSeparator && mobileSeparator}
        </>
    );
};
